import { useMemo } from 'react';
import type { DataType } from '@arcanum-hq/shared-types';
import { useConversation } from './use-conversations-v2';
import { usePublicDeal } from './use-public-deal';
import { useModel } from './use-models-v2';
import {
  useSignalFieldResolver,
  type SignalFieldResolverContext,
} from './use-signal-field-resolver';
import {
  renderValueAddPhase,
  type RenderedPhase,
} from '@/lib/value-add-narrative';

// deal-signal-overhaul Phase 4 Session 1 (2026-05-11).
//
// `useValueAddNarrative` resolves the model's `displayConfig.valueAddNarrative`
// sidecar + applies template substitution to produce render-ready phase rows
// for `<ValueAddStory>` (Phase 4 Session 3).
//
// Returns:
//   - `{ phases: RenderedPhase[], isReady: true }` when both narrative + resolver
//     have loaded. `phases.length === 3` (sidecar contract).
//   - `{ phases: [], isReady: false }` while loading.
//   - `{ phases: [], isReady: true, narrativeMissing: true }` when the model
//     has no `valueAddNarrative` configured — `<ValueAddStory>` renders an
//     "admin-fix-required" empty state.
//
// formatByKey hints per template token come from the model's schema (inputs
// + outputs); we look up each input's `dataType` for any direct named-range
// reference, else fall back to formatByDataType's String() default.

export interface UseValueAddNarrativeResult {
  phases: RenderedPhase[];
  isReady: boolean;
  /** True when the model has no narrative configured (admin-fix-required state). */
  narrativeMissing: boolean;
}

// Mirrors `<HeroMetricCell>`'s `MULTIPLE_OVERRIDE_NAMES`. Equity multiple
// outputs carry `dataType: 'currency'` in the workbook schema but always
// render as `1.26x` on the Signal page. Treat as `decimal` for narrative
// rendering (formatMultiple → `1.26x`).
const MULTIPLIER_NAMED_RANGE_OVERRIDES = [
  'EquityMultiple',
  'GPEquityMultiple',
  'LPEquityMultiple',
] as const;

export function useValueAddNarrative(
  context: SignalFieldResolverContext,
): UseValueAddNarrativeResult {
  // `deal-signal-overhaul` Phase 5 hotfix (2026-05-13): PREFER schema from
  // the resolver (covers both auth + public paths via the unified resolver
  // shape) and FALL BACK to `useModel(modelId)` for back-compat with tests
  // and any caller pre-Phase-5.
  const isAuth = context.kind === 'conversation';
  const resolver = useSignalFieldResolver(context);
  const conversation = useConversation(isAuth ? context.conversationId : null);
  const publicDeal = usePublicDeal(!isAuth ? context.shareId : null);
  const modelId = isAuth
    ? conversation.data?.data?.modelId
    : publicDeal.data?.modelId;
  const modelQuery = useModel(modelId ?? null);

  return useMemo<UseValueAddNarrativeResult>(() => {
    const modelData = resolver.schema ?? modelQuery.data?.data;
    const narrative = modelData?.displayConfig?.valueAddNarrative;
    const isReady = Boolean(modelData && resolver.isReady);

    if (!isReady) {
      return { phases: [], isReady: false, narrativeMissing: false };
    }

    if (!narrative) {
      return { phases: [], isReady: true, narrativeMissing: true };
    }

    // Build `formatByKey` so both template tokens (`{usesLandCost}` — mapping
    // key) AND raw named-range references (`{LandCost}` — escape hatch via
    // `resolveTemplate`'s direct-named-range fallback) resolve to the right
    // formatter. Three population passes:
    //
    //   1. Walk `inputs[]` — populate `formatByKey[input.name] = input.dataType`
    //      so direct named-range tokens resolve.
    //   2. Walk `outputs[]` — same pattern. The OutputDefinition schema DOES
    //      carry `dataType` (model.types.ts:78); the prior version of this
    //      hook skipped outputs based on a stale comment.
    //   3. Walk the model's `signalFieldMappings` — for each `mappingKey →
    //      namedRange` entry, set `formatByKey[mappingKey] = formatByKey[namedRange]`.
    //      This is the load-bearing pass — the value-add-narrative.json
    //      sidecars use mapping keys (e.g. `{usesLandCost}`, `{heroIRR}`),
    //      NOT raw named ranges.
    //
    // Cost: bounded (~50 inputs + ~90 outputs + ~44 mapping keys per model),
    // recomputed only when the resolver or model changes.
    const formatByKey: Record<string, DataType> = {};
    for (const input of modelData?.inputs ?? []) {
      formatByKey[input.name] = input.dataType;
    }
    for (const output of modelData?.outputs ?? []) {
      formatByKey[output.name] = output.dataType;
    }
    // `EquityMultiple` / `GPEquityMultiple` / `LPEquityMultiple` carry
    // `dataType: 'currency'` in the workbook schema but render as `1.26x`
    // (multiplier) everywhere on the Signal page. Mirror the same defensive
    // override that `<HeroMetricCell>` applies (HeroMetricCell.tsx:54-58 +
    // 96-100) so the value-add story footers match the hero strip.
    for (const overrideName of MULTIPLIER_NAMED_RANGE_OVERRIDES) {
      formatByKey[overrideName] = 'decimal';
    }
    const mappings = modelData?.displayConfig?.signalFieldMappings as
      | Record<string, string | undefined>
      | undefined;
    if (mappings) {
      for (const [mappingKey, namedRange] of Object.entries(mappings)) {
        if (!namedRange) continue;
        const fmt = formatByKey[namedRange];
        if (fmt) formatByKey[mappingKey] = fmt;
      }
    }

    const phases = narrative.phases.map((phase) =>
      renderValueAddPhase(phase, resolver.resolveTemplate, formatByKey),
    );

    return { phases, isReady: true, narrativeMissing: false };
  }, [resolver, modelQuery.data?.data]);
}
